/**
 * meetings.service.ts — Layer 3: Meetings Domain
 *
 * All meeting, transcript, recording, and notification API calls live here.
 * Components and hooks import ONLY from this file.
 */

import { getRequest, postRequest, putRequest, patchRequest, deleteRequest, deleteWithBodyRequest, postFormData, postRawResponse } from './http.service';
import {
  URL_MEETINGS,
  URL_MEETINGS_BY_ORG,
  URL_MEETINGS_BY_CLIENT,
  URL_MEETINGS_NEW,
  URL_MEETINGS_NOTIFICATIONS,
  URL_MEETINGS_NOTIFICATIONS_MARK_ALL_SEEN,
  URL_MEETINGS_NOTIFICATIONS_MARK_ALL_READ,
  URL_MEETINGS_NOTIFICATIONS_MARK_ONE_READ,
  URL_MEETINGS_POLL_MISSING_TRANSCRIPT,
  URL_MEETINGS_REDO_LIVE_TRANSCRIPT,
  URL_MEETINGS_SAVE_LIVE_TRANSCRIPT,
  URL_MEETINGS_UPDATE_SPEAKERS,
  URL_MEETINGS_UPDATE_FILE_NOTE,
  URL_MEETINGS_UPDATE_FILE_NOTE_TIMESTAMP,
  URL_MEETINGS_DELETE_RECORDING,
  URL_MEETINGS_RENAME_RECORDING,
  URL_MEETINGS_SUMMARIZE,
  URL_MEETINGS_GENERATE_FILE_NOTE,
  URL_MEETINGS_SEND_EMAIL,
  URL_MEETINGS_TRANSCRIBE_DATA,
  URL_MEETINGS_CALENDLY_EVENT_TYPES,
  URL_MEETINGS_CALENDLY_SEND_INVITE,
  URL_MEETINGS_SETTINGS,
  URL_MEETING_BY_ID,
  URL_MEETING_UPDATE,
  URL_MEETING_FILE_NOTE_TEMPLATE,
  URL_MEETING_ATTENDEES,
  URL_MEETING_FILE_NOTE,
  URL_MEETING_RECORDINGS,
  URL_MEETING_RECORDINGS_REARRANGE,
  URL_MEETING_UPLOAD_RECORDING,
  URL_MEETING_REQUEST_UPLOAD_URL,
  URL_MEETING_CONFIRM_UPLOAD,
  URL_MEETING_TRANSCRIPTS_BY_CLIENT,
  URL_MEETING_ATLAS_TRANSCRIPTS,
  URL_MEETING_CHUNK,
  URL_MEETING_CLIENT_SUMMARY,
  URL_MEETING_LAST_INTERACTION,
  URL_MEETINGS_CLIENT_MEETINGS,
  URL_MEETINGS_DISCOVERED,
  URL_OUTLOOK_CALENDAR_IMPORT,
  URL_MEETINGS_SETTINGS_BY_ID,
  URL_MEETINGS_NOTIFICATIONS_PAGED,
  URL_MEETINGS_ZOOM_STATUS,
  URL_MEETINGS_EVENTS,
  URL_MEETINGS_EVENTS_RANGE,
  URL_MEETINGS_ZOOM_DISCOVERED,
  URL_MEETINGS_CALENDAR_AUTO_IMPORT,
  URL_MEETINGS_CALENDAR_ONBOARD_IMPORT,
  // Transcript workflow
  URL_MEETINGS_INITIATE_TRANSCRIPTION,
  URL_MEETINGS_HANDLE_TRANSCRIPT_GRAPH,
  URL_MEETING_TRANSCRIPT_BY_ID,
  URL_MEETINGS_JOB_STATUS,
  URL_MEETING_FILE_NOTE_BY_ID,
  URL_MEETING_DELETE_FILE_NOTE,
  URL_MEETING_SUMMARY,
  URL_MEETING_SUMMARY_GENERATE,
  URL_MEETING_SUMMARY_EXPORT_PDF,
  URL_MEETING_TRANSCRIPT_EXPORT_PDF,
  URL_MEETING_FILE_NOTE_EXPORT_PDF,
} from './url.service';

// ---------------------------------------------------------------------------
// Meetings — CRUD
// ---------------------------------------------------------------------------

/** List all meetings */
export const fetchMeetings = (signal?: AbortSignal) =>
  getRequest(URL_MEETINGS, signal);

/** Meetings for one client, filtered server-side — for pickers already scoped to a known client. */
export const fetchMeetingsByClient = (clientId: string, signal?: AbortSignal) =>
  getRequest(URL_MEETINGS_BY_CLIENT(clientId), signal);

/** List meetings for an org, optionally filtered to one advisor */
export const fetchMeetingsByOrg = (orgId: string, advisorId?: string) =>
  getRequest(URL_MEETINGS_BY_ORG(orgId, advisorId));

/** Get a single meeting */
export const fetchMeetingById = (meetingId: string) =>
  getRequest(URL_MEETING_BY_ID(meetingId));

/** Create a new meeting */
export const createMeeting = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_NEW, payload);

/** Update meeting metadata */
export const updateMeeting = (meetingId: string, payload: Record<string, unknown>) =>
  putRequest(URL_MEETING_UPDATE(meetingId), payload);

/** Persist the meeting's default file note template (e.g. from the meeting detail page's template dropdown) */
export const updateMeetingFileNoteTemplate = (meetingId: string, fileNoteTemplateId: string | null) =>
  patchRequest(URL_MEETING_FILE_NOTE_TEMPLATE(meetingId), { fileNoteTemplateId });

/** Delete a meeting */
export const deleteMeeting = (meetingId: string) =>
  deleteRequest(URL_MEETING_BY_ID(meetingId));

// ---------------------------------------------------------------------------
// Attendees & File Notes
// ---------------------------------------------------------------------------

/** Get all attendees for a meeting */
export const fetchMeetingAttendees = (meetingId: string) =>
  getRequest(URL_MEETING_ATTENDEES(meetingId));

/** Get the file note for a meeting */
export const fetchMeetingFileNote = (meetingId: string) =>
  getRequest(URL_MEETING_FILE_NOTE(meetingId));

/** Update the file note */
export const updateFileNote = (payload: Record<string, unknown>) =>
  putRequest(URL_MEETINGS_UPDATE_FILE_NOTE, payload);

/** Update the file note timestamp (restore a version) */
export const updateFileNoteTimestamp = (payload: Record<string, unknown>) =>
  patchRequest(URL_MEETINGS_UPDATE_FILE_NOTE_TIMESTAMP, payload);

// ---------------------------------------------------------------------------
// Recordings
// ---------------------------------------------------------------------------

/** Get all recordings for a meeting */
export const fetchMeetingRecordings = (meetingId: string) =>
  getRequest(URL_MEETING_RECORDINGS(meetingId));

/** Attach a recording to a meeting */
export const addMeetingRecording = (meetingId: string, payload: Record<string, unknown>) =>
  postRequest(URL_MEETING_RECORDINGS(meetingId), payload);

/** Remove a recording */
export const deleteMeetingRecording = (meetingId: string) =>
  deleteRequest(URL_MEETING_RECORDINGS(meetingId));

/** Rearrange recording order */
export const rearrangeRecordings = (meetingId: string, payload: Record<string, unknown>) =>
  postRequest(URL_MEETING_RECORDINGS_REARRANGE(meetingId), payload);

/** Upload a recording file */
export const uploadRecording = (recordingId: string, formData: FormData) =>
  postFormData(URL_MEETING_UPLOAD_RECORDING(recordingId), formData);

/** Request a write-only Azure SAS URL for direct-to-storage upload */
export const requestMeetingUploadUrl = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETING_REQUEST_UPLOAD_URL, payload);

/** Confirm a direct-to-storage upload completed (saves metadata) */
export const confirmMeetingUpload = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETING_CONFIRM_UPLOAD, payload);

/** Delete a recording by reference (body-based deletion) */
export const deleteRecording = (payload: Record<string, unknown>) =>
  deleteWithBodyRequest(URL_MEETINGS_DELETE_RECORDING, payload);

/** Rename a recording */
export const renameRecording = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_RENAME_RECORDING, payload);

// ---------------------------------------------------------------------------
// Transcripts
// ---------------------------------------------------------------------------

/** Get all transcripts for a client */
export const fetchClientTranscripts = (clientId: string) =>
  getRequest(URL_MEETING_TRANSCRIPTS_BY_CLIENT(clientId));

/** Get Atlas-format transcripts for a client */
export const fetchAtlasTranscripts = (clientId: string) =>
  getRequest(URL_MEETING_ATLAS_TRANSCRIPTS(clientId));

/** Get a single chunk of transcript data */
export const fetchChunk = (chunkId: string) =>
  getRequest(URL_MEETING_CHUNK(chunkId));

/** Poll for any missing transcriptions and kick them off */
export const pollMissingTranscription = () =>
  postRequest(URL_MEETINGS_POLL_MISSING_TRANSCRIPT, {});

/** Redo live transcription for a meeting */
export const redoLiveTranscription = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_REDO_LIVE_TRANSCRIPT, payload);

/** Persist a transcript captured live over the streaming WebSocket */
export const saveLiveTranscript = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_SAVE_LIVE_TRANSCRIPT, payload);

/** Submit raw audio/data for transcription */
export const transcribeData = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_TRANSCRIBE_DATA, payload);

/** Get the fact-find template schema used to build transcription forms */
export const fetchTranscribeDataSchema = (signal?: AbortSignal) =>
  getRequest(URL_MEETINGS_TRANSCRIBE_DATA, signal);

// ---------------------------------------------------------------------------
// Speakers & Summary
// ---------------------------------------------------------------------------

/** Update speaker labels */
export const updateSpeakers = (payload: Record<string, unknown>) =>
  putRequest(URL_MEETINGS_UPDATE_SPEAKERS, payload);

/** Summarise a meeting */
export const summariseMeeting = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_SUMMARIZE, payload);

/**
 * Generate a file note entirely server-side from just IDs (no transcript
 * text) — replaces the old chunk_embedding + summarize two-step so the whole
 * pipeline survives the tab closing. `keepalive: true` lets this small
 * request finish transmitting even if the tab closes right after it's sent.
 */
export const generateFileNoteServerSide = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_GENERATE_FILE_NOTE, payload, { keepalive: true });

/** Get an AI-generated summary for a client across all meetings */
export const fetchClientSummary = (clientId: string) =>
  getRequest(URL_MEETING_CLIENT_SUMMARY(clientId));

/** Get the timestamp of the last interaction with a client */
export const fetchLastInteraction = (clientId: string) =>
  getRequest(URL_MEETING_LAST_INTERACTION(clientId));

/** Get all meetings for a specific client */
export const fetchClientMeetings = (clientId: string) =>
  getRequest(URL_MEETINGS_CLIENT_MEETINGS(clientId));

// ---------------------------------------------------------------------------
// Email & Calendly
// ---------------------------------------------------------------------------

/** Send the meeting summary / file-note by email */
export const sendMeetingEmail = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_SEND_EMAIL, payload);

/** Send an email with file attachments (multipart) */
export const sendMeetingEmailWithAttachments = (formData: FormData) =>
  postFormData(URL_MEETINGS_SEND_EMAIL, formData);

/** Fetch Calendly event types for the connected account */
export const fetchCalendlyEventTypes = () =>
  getRequest(URL_MEETINGS_CALENDLY_EVENT_TYPES);

/** Send a Calendly scheduling invitation to contacts */
export const sendCalendlyInvite = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_CALENDLY_SEND_INVITE, payload);

/** Update a meeting via POST (matches the /meetings/update/:id route verb) */
export const postMeetingUpdate = (meetingId: string, payload: Record<string, unknown>) =>
  postRequest(URL_MEETING_UPDATE(meetingId), payload);

/** Check whether Zoom is enabled for an org (New Meeting form) */
export const checkOrgZoomStatus = (orgId: string) =>
  getRequest(URL_MEETINGS_ZOOM_STATUS(orgId));

/** Get calendar events (with attendee info) for the New Meeting date picker */
export const fetchMeetingEvents = (timezone: string) =>
  getRequest(URL_MEETINGS_EVENTS(timezone));

/** Get calendar events across an explicit window (home calendar view) */
export const fetchMeetingEventsInRange = (timezone: string, start: string, end: string) =>
  getRequest(URL_MEETINGS_EVENTS_RANGE(timezone, start, end));

/** Get Zoom-only meetings (scheduled in Zoom, never on a calendar) for a window */
export const fetchZoomDiscoveredMeetings = (start: string, end: string) =>
  getRequest(URL_MEETINGS_ZOOM_DISCOVERED(start, end));

/** Auto-import 1-1s with known clients and classify the remaining events */
export const autoImportCalendarMeetings = (payload: { events: any[]; timezone: string }) =>
  postRequest(URL_MEETINGS_CALENDAR_AUTO_IMPORT, payload);

/**
 * Create contact + client for a new 1-1 counterparty, then file the meeting.
 * Pass `event` to import a calendar event, or `meetingId` to assign a client to
 * a meeting that already exists (an unassigned Calendly booking).
 */
export const onboardAndImportMeeting = (payload: {
  event?: any;
  meetingId?: string;
  contactName: string;
  contactEmail: string;
  clientName: string;
  entityType?: string;
  timezone: string;
}) => postRequest(URL_MEETINGS_CALENDAR_ONBOARD_IMPORT, payload);

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

/** Get meeting notifications (latest page) */
export const fetchNotifications = () =>
  getRequest(URL_MEETINGS_NOTIFICATIONS);

/** Get meeting notifications for a specific page */
export const fetchNotificationsPaged = (page: number, limit: number) =>
  getRequest(URL_MEETINGS_NOTIFICATIONS_PAGED(page, limit));

/** Mark all notifications as seen */
export const markAllNotificationsSeen = () =>
  postRequest(URL_MEETINGS_NOTIFICATIONS_MARK_ALL_SEEN, {});

/** Mark all notifications as read */
export const markAllNotificationsRead = () =>
  postRequest(URL_MEETINGS_NOTIFICATIONS_MARK_ALL_READ, {});

/** Mark a single notification as read (identified by its recording ID) */
export const markNotificationRead = (recordingId: string) =>
  postRequest(URL_MEETINGS_NOTIFICATIONS_MARK_ONE_READ, { recordingId });

/** Delete a notification */
export const deleteNotification = (notificationId: string) =>
  deleteRequest(`${URL_MEETINGS_NOTIFICATIONS}/${notificationId}`);

/** Clear all notifications for the current user */
export const clearAllNotifications = () =>
  deleteRequest(URL_MEETINGS_NOTIFICATIONS);

// ---------------------------------------------------------------------------
// Transcript workflow (initiation, editing, job polling, file note generation)
// ---------------------------------------------------------------------------

/** Start a new transcription job for a recording */
export const initiateTranscription = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_INITIATE_TRANSCRIPTION, payload);

/** Update individual transcript lines for a transcription */
export const updateTranscriptLines = (transcriptionId: string, payload: Record<string, unknown>) =>
  patchRequest(URL_MEETING_TRANSCRIPT_BY_ID(transcriptionId), payload);

/** Update speaker labels (PATCH — matches component usage) */
export const patchSpeakers = (payload: Record<string, unknown>) =>
  patchRequest(URL_MEETINGS_UPDATE_SPEAKERS, payload);

/** Poll async job status (file note generation, graph update) */
export const fetchJobStatus = (jobId: string) =>
  getRequest(URL_MEETINGS_JOB_STATUS(jobId));

/** Fetch a generated file note by its transcription ID */
export const fetchFileNoteById = (transcriptionId: string) =>
  getRequest(URL_MEETING_FILE_NOTE_BY_ID(transcriptionId));

/** Save/update file note content (POST — matches existing backend route) */
export const saveFileNote = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_UPDATE_FILE_NOTE, payload);

/** Delete a file note and all its versions */
export const deleteFileNote = (fileNoteId: string) =>
  deleteRequest(URL_MEETING_DELETE_FILE_NOTE(fileNoteId));

/** Trigger transcript-based knowledge-graph update */
export const handleTranscriptGraph = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_HANDLE_TRANSCRIPT_GRAPH, payload);

/** Poll for completion of specific transcription IDs */
export const pollTranscriptions = (transcriptionIds: string[]) =>
  postRequest(URL_MEETINGS_POLL_MISSING_TRANSCRIPT, { transcriptionIds });

// ---------------------------------------------------------------------------
// Meeting Settings
// ---------------------------------------------------------------------------

/** Get meeting settings for the org */
export const fetchMeetingSettings = () =>
  getRequest(URL_MEETINGS_SETTINGS);

/** Save new meeting settings */
export const createMeetingSettings = (payload: Record<string, unknown>) =>
  postRequest(URL_MEETINGS_SETTINGS, payload);

/** Update existing meeting settings */
export const updateMeetingSettings = (settingsId: string, payload: Record<string, unknown>) =>
  putRequest(URL_MEETINGS_SETTINGS_BY_ID(settingsId), payload);

/** Delete meeting settings */
export const deleteMeetingSettings = (settingsId: string) =>
  deleteRequest(URL_MEETINGS_SETTINGS_BY_ID(settingsId));

// ---------------------------------------------------------------------------
// General Meeting Summary
// ---------------------------------------------------------------------------

/** Trigger AI summary generation for a general meeting */
export const generateMeetingSummary = (meetingId: string) =>
  postRequest(URL_MEETING_SUMMARY_GENERATE(meetingId), {});

/** Fetch the saved summary for a general meeting */
export const fetchMeetingSummary = (meetingId: string) =>
  getRequest(URL_MEETING_SUMMARY(meetingId));

/** Persist user edits to a meeting summary */
export const updateMeetingSummary = (meetingId: string, payload: Record<string, unknown>) =>
  putRequest(URL_MEETING_SUMMARY(meetingId), payload);

/** Export AI meeting summary as PDF (Typst) */
export const exportMeetingSummaryPdf = (meetingId: string) =>
  postRawResponse(URL_MEETING_SUMMARY_EXPORT_PDF(meetingId), {});

/** Export transcript as PDF (Typst) */
export const exportTranscriptPdf = (meetingId: string, transcriptId: string) =>
  postRawResponse(URL_MEETING_TRANSCRIPT_EXPORT_PDF(meetingId, transcriptId), {});

/** Export financial meeting file note as PDF (Typst) */
export const exportFileNotePdf = (meetingId: string, fileNoteId: string) =>
  postRawResponse(URL_MEETING_FILE_NOTE_EXPORT_PDF(meetingId, fileNoteId), {});

// ─── Calendar meeting discovery ──────────────────────────────────────────────

/** Calendar meetings involving this client's linked contacts that aren't linked to Prospo yet. */
export const fetchDiscoveredMeetings = (clientId: string) =>
  getRequest(URL_MEETINGS_DISCOVERED(clientId));

/**
 * Promote a discovered calendar meeting into a real Prospo meeting record.
 *
 * No attendee list is sent: the backend attaches only the contacts already
 * linked to this client, matched against the event's own attendees. It never
 * creates contacts or client links, so there is nothing for the caller to
 * choose — and nothing a request could smuggle in.
 */
export const linkDiscoveredMeeting = (payload: {
  event: unknown;
  clientId: string;
  timezone: string;
}) => postRequest(URL_OUTLOOK_CALENDAR_IMPORT, payload);
